import { AuthenticationManager } from '@frontend/authentication-v2';
import { CheckboxInput } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import { useBrandingRepository } from '@frontend/repository';
import { IUser } from '@frontend/user/types';
import { useMemo } from 'react';
import { LuExternalLink } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import defaultLogo from '../../../../assets/images/telloport_logo_horizontal.png';
import useAcceptEula from './accept-eula.controller';

const ID = 'accept-eula';
export interface AcceptEulaProps {
    user: IUser;
    callback?: () => void;
    overrideUrl?: string | null;
}

const AcceptEula = (props: AcceptEulaProps) => {
    const viewProps = useAcceptEula(props);
    const { logo_navbar_expanded } = useBrandingRepository();

    const logo = useMemo(() => {
        if (logo_navbar_expanded) return logo_navbar_expanded;
        return defaultLogo;
    }, [logo_navbar_expanded]);

    return (
        <div
            id={ID}
            className='relative flex h-screen w-screen flex-col'>
            <div className='bg-brand-structure absolute top-0 right-0 left-0 -z-1 h-[40vh] w-full'></div>
            <div className='bg-always-white mx-auto my-40 flex h-1/2 w-1/2 flex-col rounded-lg shadow-lg'>
                <div className='flex h-2/4 flex-col justify-center p-3 align-middle'>
                    <img
                        src={logo}
                        className='img-fluid m-auto w-30'
                    />
                    <div className='flex flex-col items-center justify-center gap-4 p-4'>
                        <h1 className='text-brand-primary m-auto text-4xl font-black'>
                            <FormattedMessage
                                id='eula.user-agreement'
                                defaultMessage='User agreement'
                            />
                        </h1>
                        <p className='text-always-black m-auto text-base font-medium'>
                            <FormattedMessage
                                id='eula.welcome-message'
                                defaultMessage='Welcome! Before you start using the app, please take a moment to review our End User License Agreement (EULA).'
                            />
                        </p>
                        <p className='text-always-black justify-center text-base font-medium'>
                            <FormattedMessage
                                id='eula.outline-message'
                                defaultMessage='It outlines your rights, responsibilities, and how we keep things running smoothly.'
                            />
                        </p>
                    </div>
                </div>
                <div className='flex h-1/4 w-full flex-col content-center items-center p-4'>
                    <div className='flex flex-col items-start justify-start gap-6 self-stretch'>
                        <div className='flex w-full flex-col items-center justify-start gap-4'>
                            <div
                                onClick={viewProps.onClickEulaLink}
                                className='bg-background-surface border-border-default flex w-1/2 cursor-pointer flex-row items-center justify-between gap-5 rounded-md border px-4 py-3'>
                                <div className='text-foreground-default justify-start text-base font-medium'>
                                    <FormattedMessage
                                        defaultMessage='End User License Agreement'
                                        id='eula.end-user-license-agreement'
                                    />
                                </div>
                                <LuExternalLink className='text-brand-primary size-5' />
                            </div>

                            <CheckboxInput
                                id='accept-eula-checkbox'
                                className='text-always-black'
                                label={
                                    <FormattedMessage
                                        id='eula.accept-terms'
                                        defaultMessage='I have read and agree to the End User License Agreement.'
                                    />
                                }
                                value={viewProps.accepted}
                                onChange={(e) => viewProps.setAccepted(e)}
                            />
                        </div>
                    </div>
                </div>

                <div className='mt-2 flex h-1/4 w-full flex-col content-center items-center gap-3'>
                    <button
                        id='submit-button'
                        type='submit'
                        data-testid='signInButton'
                        className='btn bg-brand-primary disabled:bg-brand-muted/35 w-1/3'
                        disabled={!viewProps.accepted}
                        onClick={viewProps.onSubmit}>
                        <div className='text-always-white justify-start text-base font-medium'>{CommonMessage.BUTTONS.CONTINUE}</div>
                    </button>
                    <span
                        id='forgot-password-button'
                        onClick={() => {
                            AuthenticationManager.getInstance().logout(() => {
                                window.location.reload();
                            });
                        }}
                        data-testid='forgotPasswordLink'
                        className='text-brand-muted cursor-pointer font-medium'>
                        {CommonMessage.BUTTONS.GO_BACK}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default AcceptEula;
